import React, { useState } from 'react';
import {
  Typography,
  Container,
  Grid,
  Box,
  Card,
  CardContent,
  CardActions,
  Button,
  useTheme,
  CardHeader,
  ListItem,
  ListItemIcon,
  ListItemText,
  List,
  Chip,
  Stack,
  Switch,
  FormControlLabel,
} from '@mui/material';

import { 
  CheckCircle, 
  Business, 
  ProductionQuantityLimits,
  PrecisionManufacturing,
  AutoAwesome,
} from '@mui/icons-material';

interface PricingPlan {
  title: string;
  subtitle: string;
  price: {
    monthly: string;
    yearly: string;
  };
  description: string;
  features: string[];
  category: string;
  isPopular: boolean;
  icon: React.ReactNode;
}

const pricingPlans: PricingPlan[] = [
  {
    title: 'Starter Manufacturing',
    subtitle: 'Perfect for small businesses and startups',
    price: {
      monthly: 'KES 50,000',
      yearly: 'KES 500,000',
    },
    description: 'Essential manufacturing services for growing businesses',
    category: 'Basic',
    isPopular: false,
    icon: <Business sx={{ fontSize: 40, color: '#6366f1' }} />,
    features: [
      'Custom product design consultation',
      'Small batch production (up to 100 units)',
      'Basic quality control testing',
      'Standard material options',
      'Email and phone support',
      '2-week delivery timeline',
      'Basic packaging and labeling',
      '1 revision included',
    ],
  },
  {
    title: 'Professional Manufacturing',
    subtitle: 'Ideal for established businesses',
    price: {
      monthly: 'KES 150,000',
      yearly: 'KES 1,500,000',
    },
    description: 'Comprehensive manufacturing solutions with premium features',
    category: 'Professional',
    isPopular: true,
    icon: <ProductionQuantityLimits sx={{ fontSize: 40, color: '#06b6d4' }} />,
    features: [
      'Advanced product development',
      'Medium batch production (up to 1,000 units)',
      'Comprehensive quality assurance',
      'Premium material selection',
      'Priority customer support',
      '1-week delivery timeline',
      'Custom packaging design',
      '3 revisions included',
      'Supply chain optimization',
      'Monthly progress reports',
    ],
  },
  {
    title: 'Enterprise Manufacturing',
    subtitle: 'For large-scale operations',
    price: {
      monthly: 'KES 500,000',
      yearly: 'KES 5,000,000',
    },
    description: 'Full-service manufacturing with dedicated support',
    category: 'Enterprise',
    isPopular: false,
    icon: <PrecisionManufacturing sx={{ fontSize: 40, color: '#f59e0b' }} />,
    features: [
      'Full product lifecycle management',
      'Large batch production (unlimited)',
      'ISO-certified quality control',
      'Exclusive material sourcing',
      'Dedicated account manager',
      'Custom delivery schedules',
      'Premium packaging solutions',
      'Unlimited revisions',
      'Supply chain management',
      'Weekly progress reports',
      'Custom payment terms',
      'Priority production scheduling',
    ],
  },
];

const serviceCategories = [
  { name: 'All Services', value: 'all', icon: <Business /> },
  { name: 'Textile Manufacturing', value: 'textiles', icon: <Business /> },
  { name: 'Furniture Production', value: 'furniture', icon: <ProductionQuantityLimits /> },
  { name: 'Metalworks', value: 'metalworks', icon: <PrecisionManufacturing /> },
  { name: 'Custom Solutions', value: 'custom', icon: <AutoAwesome /> },
];

export const Pricing: React.FC = () => {
  const theme = useTheme();
  const [isYearly, setIsYearly] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');

  return (
    <Box sx={{
      minHeight: '100vh',
      bgcolor: 'background.default',
      pt: 12,
      pb: 16,
    }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1b4d89 0%, #0f3460 100%)',
          color: 'white',
          py: 12,
          mb: 8,
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h1"
            component="h1"
            gutterBottom
            sx={{
              background: 'linear-gradient(135deg, #ffffff 0%, #e0e7ff 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 800,
              mb: 4,
              textAlign: 'center',
            }}
          >
            Transparent Manufacturing Pricing
          </Typography>
          <Typography variant="h5" sx={{ mb: 4, opacity: 0.9, textAlign: 'center' }}>
            Choose the manufacturing plan that fits your business needs
          </Typography>
          <Typography variant="body1" sx={{ maxWidth: '800px', mx: 'auto', opacity: 0.8, textAlign: 'center' }}>
            From small batch production to large-scale manufacturing, we offer flexible pricing options 
            to help your business grow and succeed in the African market.
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg">
        {/* Service Categories */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 4, textAlign: 'center' }}>
            Our Manufacturing Services
          </Typography>
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            spacing={2} 
            flexWrap="wrap"
            justifyContent="center"
            sx={{ mb: 6 }}
          >
            {serviceCategories.map((category) => (
              <Chip
                key={category.value}
                label={category.name}
                onClick={() => setSelectedCategory(category.value)}
                icon={category.icon}
                sx={{
                  background: selectedCategory === category.value 
                    ? 'linear-gradient(135deg, #1b4d89 0%, #0f3460 100%)'
                    : 'rgba(255, 255, 255, 0.1)',
                  color: selectedCategory === category.value ? 'white' : 'text.primary',
                  border: selectedCategory === category.value 
                    ? 'none' 
                    : '1px solid rgba(99, 102, 241, 0.2)',
                  '&:hover': {
                    background: selectedCategory === category.value 
                      ? 'linear-gradient(135deg, #0f3460 0%, #1b4d89 100%)'
                      : 'rgba(99, 102, 241, 0.1)',
                  },
                }}
              />
            ))}
          </Stack>
        </Box>

        {/* Billing Toggle */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography variant="h6" gutterBottom>
            Choose Your Billing Cycle
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center" justifyContent="center">
            <Typography variant="body1" color={!isYearly ? 'text.primary' : 'text.secondary'}>
              Monthly
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={isYearly}
                  onChange={(e) => setIsYearly(e.target.checked)}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#6366f1',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#6366f1',
                    },
                  }}
                />
              }
              label=""
            />
            <Typography variant="body1" color={isYearly ? 'text.primary' : 'text.secondary'}>
              Yearly
            </Typography>
            {isYearly && (
              <Chip 
                label="Save 20%" 
                size="small" 
                sx={{ 
                  background: '#10b981',
                  color: 'white',
                  fontWeight: 600,
                }} 
              />
            )}
          </Stack>
        </Box>

        {/* Pricing Cards */}
        <Grid container spacing={4}>
          {pricingPlans.map((plan, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Card
                sx={{
                  height: '100%',
                  background: plan.isPopular 
                    ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.05) 0%, rgba(6, 182, 212, 0.05) 100%)'
                    : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.98) 100%)',
                  backdropFilter: 'blur(10px)',
                  border: plan.isPopular 
                    ? '2px solid #6366f1'
                    : '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: 3,
                  boxShadow: plan.isPopular 
                    ? '0px 25px 50px rgba(99, 102, 241, 0.15)'
                    : '0px 20px 25px rgba(0, 0, 0, 0.1), 0px 10px 10px rgba(0, 0, 0, 0.04)',
                  transition: 'all 0.3s ease',
                  overflow: 'hidden',
                  position: 'relative',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0px 25px 50px rgba(0, 0, 0, 0.15)',
                  },
                }}
              >
                {plan.isPopular && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      background: 'linear-gradient(135deg, #1b4d89 0%, #0f3460 100%)',
                      color: 'white',
                      textAlign: 'center',
                      py: 1,
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      zIndex: 1,
                    }}
                  >
                    Most Popular
                  </Box>
                )}

                <CardContent sx={{ p: 4, pt: plan.isPopular ? 6 : 4 }}>
                  <Box sx={{ textAlign: 'center', mb: 3 }}>
                    {plan.icon}
                  </Box>

                  <Chip 
                    label={plan.category} 
                    size="small" 
                    sx={{ 
                      mb: 2,
                      background: 'rgba(99, 102, 241, 0.1)',
                      color: '#6366f1',
                      fontWeight: 500,
                    }} 
                  />

                  <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, mb: 1 }}>
                    {plan.title}
                  </Typography>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    {plan.subtitle}
                  </Typography>

                  <Box sx={{ textAlign: 'center', mb: 4 }}>
                    <Typography variant="h2" sx={{ fontWeight: 800, color: '#6366f1', mb: 1 }}>
                      {isYearly ? plan.price.yearly : plan.price.monthly}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {isYearly ? 'per year' : 'per month'}
                    </Typography>
                  </Box>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    {plan.description}
                  </Typography>

                  <List dense>
                    {plan.features.map((feature, i) => (
                      <ListItem key={i} sx={{ px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 24 }}>
                          <CheckCircle sx={{ fontSize: 16, color: '#10b981' }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary={feature} 
                          primaryTypographyProps={{ 
                            variant: 'body2',
                            sx: { fontSize: '0.875rem' }
                          }} 
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>

                <CardActions sx={{ p: 4, pt: 0 }}>
                  <Button
                    variant={plan.isPopular ? 'contained' : 'outlined'}
                    size="large"
                    fullWidth
                    sx={{
                      py: 2,
                      borderRadius: 2,
                      background: plan.isPopular 
                        ? 'linear-gradient(135deg, #1b4d89 0%, #0f3460 100%)'
                        : 'transparent',
                      border: plan.isPopular 
                        ? 'none'
                        : '2px solid #6366f1',
                      color: plan.isPopular ? 'white' : '#6366f1',
                      '&:hover': {
                        background: plan.isPopular 
                          ? 'linear-gradient(135deg, #0f3460 0%, #1b4d89 100%)'
                          : 'rgba(99, 102, 241, 0.04)',
                      },
                    }}
                  >
                    {plan.isPopular ? 'Get Started' : 'Choose Plan'}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Additional Information */}
        <Box sx={{ 
          mt: 8, 
          p: 6, 
          background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)', 
          borderRadius: 3,
          textAlign: 'center',
        }}>
          <Typography variant="h3" gutterBottom sx={{ fontWeight: 700 }}>
            Need a Custom Solution?
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 4, maxWidth: '600px', mx: 'auto' }}>
            We offer custom manufacturing solutions tailored to your specific requirements. 
            Contact us for a personalized quote.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} justifyContent="center">
            <Button
              variant="contained"
              size="large"
              sx={{
                px: 6,
                py: 2,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #1b4d89 0%, #0f3460 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #0f3460 0%, #1b4d89 100%)',
                },
              }}
            >
              Request Custom Quote
            </Button>
            <Button
              variant="outlined"
              size="large"
              sx={{
                px: 6,
                py: 2,
                borderRadius: 3,
                border: '2px solid #6366f1',
                color: '#6366f1',
                '&:hover': {
                  border: '2px solid #4f46e5',
                  background: 'rgba(99, 102, 241, 0.04)',
                },
              }}
            >
              Schedule Consultation
            </Button>
          </Stack>
        </Box>
      </Container>
    </Box>
  );
};
